// src/components/Remove3.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Popup from "reactjs-popup";
import { FaTimesCircle } from "react-icons/fa";
import {
  createMeterReport,
  updateMeterReport,
  fetchMeterBrands,
  fetchDiameters,
  fetchQnoms,
  uploadMeterReportImage,
  removeMeter,
  searchMeters,
} from "../../data/fetchData";
import { useNotification } from "../../context/NotificationContext";
import { normalizeDecimal } from "../../utils/formatters";

const TYPE_OPTIONS = [
  { value: "MAIN_GAUGE", label: "Põhiarvesti" },
  { value: "SUB_GAUGE", label: "Alamarvesti" },
  { value: "WATERING", label: "Kastmine" },
];

const fieldLabels = {
  meterNumber: "Arvesti number",
  address: "Kinnistu",
  owner: "Omanik",
  brand: "Arvesti mark",
  qn: "Qn/m³",
  diameter: "Läbimõõt",
  lastCertificationDate: "Viimane taatlemine",
  reading: "Arvesti näit",
  removalDate: "Eemaldamise kuupäev",
  type: "Töö liik",
  workType: "Töö tüüp",
  notes: "Märkused",
  images: "Pildid",
  signature: "Allkiri",
};

export default function Remove3() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [data, setData] = useState({});
  const [brandOptions, setBrandOptions] = useState([]);
  const [diameterOptions, setDiameterOptions] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);

  // load meter‐brand and diameter lists
  useEffect(() => {
    fetchMeterBrands().then(setBrandOptions).catch(console.error);
    fetchDiameters().then(setDiameterOptions).catch(console.error);
    fetchQnoms().then(setQnOptions).catch(console.error);
  }, []);

  // load our draft
  useEffect(() => {
    const raw = localStorage.getItem("removeData");
    if (raw) setData(JSON.parse(raw));
  }, []);

  // helpers
  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt.toLocaleDateString("et-EE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
  const toDateOnly = (x) => {
    if (!x) return null;
    // If already in YYYY-MM-DD, trust it
    if (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x)) return x;
    const d = new Date(x);
    if (isNaN(d)) return null;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };

  // helper: base64 → Blob (same as in Add1.js)
  function dataURLtoBlob(dataURL) {
    const [header, base64] = dataURL.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  const handleSendToDatabase = async () => {
    // Validate required fields
    if (!data.estate) {
      showError("Kinnistu info puudub");
      return;
    }
    if (!data.client) {
      showError("Kliendi info puudub");
      return;
    }
    if (!data.meterNumber) {
      showError("Arvesti number puudub");
      return;
    }
    if (!data.reading) {
      showError("Arvesti näit puudub");
      return;
    }

    // Fix invalid brand value (sometimes backend sends meterNumber as brand)
    let validBrand = data.brand;
    if (data.brand && data.brand.toString() === data.meterNumber.toString()) {
      validBrand = null;
    }

    const payload = {
      action: "remove",
      client: Number(data.client),
      estate: Number(data.estate),
      meter_brand: validBrand ? Number(validBrand) : null,
      meter_number: data.meterNumber?.toString().trim() || "",
      q_nom: data.qn ? Number(data.qn) : null,
      diameter: data.diameter ? Number(data.diameter) : null,
      type: data.type,
      last_certification_date: toDateOnly(data.lastCertificationDate),
      verification_date: toDateOnly(data.lastCertificationDate),
      seal_number: null,
      on_console: true,
      meter_node_compliant: null,
      reading: Number(normalizeDecimal(data.reading)),
      // Send date as YYYY-MM-DD to avoid timezone shifts
      date: toDateOnly(data.removalDate),
      signature: data.signature,
      status: data.signature ? "SIGNED" : "CONFIRMED",
      notes: data.notes || null,
    };

    try {
      let result;

      if (data.id) {
        result = await updateMeterReport(data.id, payload);
        showSuccess("Raport uuendatud edukalt.");
      } else {
        result = await createMeterReport(payload);
        showSuccess("Raport salvestatud edukalt.");
      }

      // --- Ensure we have meterId; if missing, attempt to fetch it by meter number ---
      let meterIdToUse = data.meterId;
      if (!meterIdToUse && data.meterNumber) {
        try {
          const meterList = await searchMeters(data.meterNumber, true); // search active meters
          if (meterList && meterList.length) {
            // Prefer exact match by system_number
            const match = meterList.find((m) => m.system_number === data.meterNumber);
            meterIdToUse = (match || meterList[0]).id;
          }
        } catch (idErr) {
          console.warn("Unable to fetch meterId via searchMeters:", idErr);
        }
      }

      // Now call the removal API for the physical meter operations (using dedicated API endpoint)
      if (result?.id && meterIdToUse) {
        try {
          const finalReadingStr = normalizeDecimal(data.reading);

          const removalResult = await removeMeter(
            meterIdToUse,
            result.id,
            finalReadingStr
          );
          showSuccess("Meter eemaldamine lõpetatud edukalt!");
        } catch (removalErr) {
          console.error("Removal API error:", removalErr);
          console.error("Error details:", removalErr.response?.data);

          // Provide more specific error messages
          if (removalErr.response?.status === 404) {
            showError("Meter või aruanne ei leitud. Kontrollige andmeid.");
          } else if (removalErr.response?.status === 400) {
            const errorMsg =
              removalErr.response?.data?.error ||
              removalErr.response?.data?.detail ||
              "Vigased andmed";
            showError(`Vigased andmed: ${errorMsg}`);
          } else {
            showError(
              `Meter eemaldamine ebaõnnestus: ${
                removalErr.response?.data?.error || removalErr.message
              }`
            );
          }

          // Continue with image upload even if removal fails since report was created
        }
      } else {
        console.warn("Cannot call removal API - missing meter ID or report ID");
        showError(
          "Ei saa meterit eemaldada - puudulikud andmed. Aruanne on loodud, kuid mõõturit ei arhiveeritud."
        );
      }

      // Upload images if any exist (same logic as Add1.js and Add3.js)
      if (data.images && data.images.length > 0 && result?.id) {
        const reportPk = result.id;
        let uploadIndex = 1;

        for (const src of data.images) {
          if (!src.startsWith("data:")) {
            // existing image (fetched for preview), skip it
            continue;
          }

          try {
            const blob = dataURLtoBlob(src);
            const filename = `report-${result.number}-${uploadIndex}.png`;
            await uploadMeterReportImage(reportPk, blob, filename);
            uploadIndex++;
          } catch (imgErr) {
            console.error(`Failed to upload image ${uploadIndex}:`, imgErr);
            // Don't throw here - continue with other images
          }
        }
      }

      localStorage.removeItem("removeData");
      navigate("/works");
    } catch (err) {
      console.error("Error:", err);
      console.error("Error response:", err.response);
      console.error("Error response data:", err.response?.data);
      showError(
        `Raporti üleslaadimine nurjus: ${
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "Tundmatu viga"
        }`
      );
    }
  };

  const handleBackEdit = () => {
    navigate("/Remove2", { state: { work: data } });
  };

  // turn a raw key/value into readable
  const renderValue = (key, val) => {
    if (["lastCertificationDate", "removalDate"].includes(key)) {
      return fmtDate(val);
    }
    if (key === "type") {
      return TYPE_OPTIONS.find((o) => o.value === val)?.label || val;
    }
    if (key === "brand") {
      return brandOptions.find((b) => b.id === Number(val))?.name || "";
    }
    if (key === "qn") {
      const q = qnOptions.find((x) => x.id === Number(val));
      return q?.q_nom ?? "";
    }
    if (key === "diameter") {
      return diameterOptions.find((d) => d.id === Number(val))?.diameter || "";
    }
    return val;
  };

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <div className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Kokkuvõte</h1>
      </div>

      <table className="w-full table-auto mt-4">
        <tbody>
          {Object.entries(data).map(([key, val]) => {
            if (["id", "images", "signature"].includes(key)) return null;
            if (!fieldLabels[key]) return null;
            return (
              <tr key={key}>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  {fieldLabels[key]}
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {renderValue(key, val)}
                </td>
              </tr>
            );
          })}

          {data.images?.length > 0 && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200 text-sm">
                Pildid
              </th>
              <td className="p-2 border-t border-gray-200">
                <div className="flex space-x-4 flex-wrap">
                  {data.images.map((src, i) => (
                    <Popup
                      key={i}
                      modal
                      trigger={
                        <img
                          src={src}
                          alt={`Foto ${i + 1}`}
                          className="w-24 h-24 object-cover rounded-md cursor-pointer"
                        />
                      }
                      closeOnDocumentClick
                    >
                      {(close) => (
                        <div className="relative p-4">
                          <button
                            onClick={close}
                            className="absolute top-2 right-2 text-2xl text-secondary bg-white rounded-full p-1"
                          >
                            <FaTimesCircle size={32} />
                          </button>
                          <img
                            src={src}
                            alt={`Foto ${i + 1}`}
                            className="w-full h-auto max-w-3xl mx-auto"
                          />
                        </div>
                      )}
                    </Popup>
                  ))}
                </div>
              </td>
            </tr>
          )}

          {data.signature && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200 text-sm">
                Allkiri
              </th>
              <td className="p-2 border-t border-gray-200 text-sm">
                <Popup
                  modal
                  trigger={
                    <img
                      src={data.signature}
                      alt="Allkiri"
                      className="w-24 h-24 object-cover rounded-md cursor-pointer"
                    />
                  }
                  closeOnDocumentClick
                >
                  {(close) => (
                    <div className="relative p-4">
                      <button
                        onClick={close}
                        className="absolute top-2 right-2 text-2xl text-secondary bg-white rounded-full p-1"
                      >
                        <FaTimesCircle size={32} />
                      </button>
                      <img
                        src={data.signature}
                        alt="Allkiri"
                        className="w-full h-auto max-w-3xl mx-auto"
                      />
                    </div>
                  )}
                </Popup>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleBackEdit}
          className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
        >
          Tagasi
        </button>
        <button
          onClick={handleSendToDatabase}
          className="bg-primary text-white py-3 px-6 rounded-md hover:bg-primary-hover"
        >
          Kinnita akt
        </button>
      </div>
    </div>
  );
}
